import { Dialog } from '@mui/material';
import { getCompanyConfig } from 'lib/api/company';
import { getDocumentsWithArrearCalculation } from 'lib/api/document';
import NomineeExporter from 'lib/common/NomineeExporter';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import { normalizeDate } from 'lib/functions/utils';
import ApplicationsInOperationWarnings, {
  ComparisonRow,
} from 'pages/Finance/Components/Financing/common/applicationsInOperationWarnings';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import GeneratePayoutStep from '../../../Create/GeneratePayouts';

const headers: { key: string; label: string; type?: any }[] = [
  { key: 'businessOriginName', label: 'Cuenta Origen' },
  { key: 'bankAccountOriginName', label: 'Banco Origen' },
  { key: 'businessDestinationName', label: 'Cuenta Destino' },
  { key: 'bankAccountDestinationName', label: 'Banco Destino' },
  { key: 'executedAt', label: 'Giro', type: 'date' },
  { key: 'amount', label: 'Monto', type: 'money' },
];

const PayOutDetailPayouts = (props: any) => {
  const { request, payments, sourceId, setSuccess, setFailure, reset } = props;
  const dispatch = useDispatch();
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [generateNomineeOpen, setGenerateNomineeOpen] = useState(false);
  const [nomineeExporterOpen, setNomineeExporterOpen] = useState(false);

  const cantSelectRow = (row: any) => {
    return row.executedAt;
  };

  const canSelectAll = () => {
    return !payments.every((payment: any) => payment.executedAt);
  };

  function handleSuccesStep(stepFunction: () => void) {
    const auxFunction = () => {
      stepFunction();
      setSuccess('Pago configurado con éxito');
      reset();
    };
    return auxFunction;
  }

  const documentsAppliedIds =
    request?.documentsApplied?.map(
      (d: { documentId: string }) => d.documentId,
    ) || [];
  const operationDate = new Date(request?.createdAt);
  const {
    data: documentsAppliedWithArrearCalculation,
    isLoading: isLoadingDocumentsAppliedWithArrearCalculation,
  } = getDocumentsWithArrearCalculation(
    dispatch,
    documentsAppliedIds,
    operationDate,
  );
  const {
    data: allowOperationIfFutureConciliationsValue,
    isLoading: isLoadingAllowOperationIfFutureConciliations,
  } = getCompanyConfig(dispatch, 'allow_operation_if_future_conciliations');
  const allowOperationIfFutureConciliations =
    allowOperationIfFutureConciliationsValue === '1';

  const isLoading =
    isLoadingDocumentsAppliedWithArrearCalculation ||
    isLoadingAllowOperationIfFutureConciliations;

  const documentsToApply = request?.documentsApplied ?? [];
  const comparisonRows: ComparisonRow[] =
    documentsAppliedWithArrearCalculation?.documents
      ? documentsToApply.map((doc: { documentId: string; amount?: number }) => {
          const withArrear =
            documentsAppliedWithArrearCalculation.documents.find(
              (d) => d.id === doc.documentId,
            );
          const lastConciliation =
            withArrear?.conciliationLog?.length &&
            withArrear?.conciliationLog?.length > 0
              ? withArrear?.conciliationLog[
                  withArrear?.conciliationLog?.length - 1
                ]
              : null;
          const hasFutureConciliations =
            lastConciliation &&
            normalizeDate(new Date(lastConciliation.conciliationDate)) >
              normalizeDate(operationDate);
          return {
            id: doc.documentId,
            folio: withArrear?.folio ?? doc.documentId,
            amountToConciliate: doc.amount ?? 0,
            totalRemainingNet: withArrear?.totalRemainingNet ?? 0,
            hasFutureConciliations,
          };
        })
      : [];
  const comparisonsWithWarnings = comparisonRows.filter(
    (row) =>
      row.amountToConciliate !== row.totalRemainingNet ||
      row.hasFutureConciliations,
  );
  const hasAmountDifferences = comparisonRows.some(
    (row) => row.amountToConciliate !== row.totalRemainingNet,
  );
  const hasFutureConciliations = comparisonRows.some(
    (row) => row.hasFutureConciliations,
  );
  const hasComparisonWarnings = hasAmountDifferences || hasFutureConciliations;
  const cantContinue =
    hasFutureConciliations && !allowOperationIfFutureConciliations;

  const buttons: CustomButtonProps[] = [
    {
      action: () => setGenerateNomineeOpen(true),
      name: 'Agregar',
      color: 'primary',
      disabled: payments.length > 0 || cantContinue,
    },
    {
      action: () => setNomineeExporterOpen(true),
      name: 'Exportar',
      color: 'secondary',
    },
  ];

  if (isLoading) return <CenteredCircularProgress />;
  return (
    <div>
      {documentsAppliedWithArrearCalculation &&
        comparisonRows.length > 0 &&
        hasComparisonWarnings && (
          <ApplicationsInOperationWarnings
            hasAmountDifferences={hasAmountDifferences}
            hasFutureConciliations={hasFutureConciliations}
            allowOperationIfFutureConciliations={
              allowOperationIfFutureConciliations
            }
            comparisonsWithWarnings={comparisonsWithWarnings}
          />
        )}
      <HeaderWithActions
        title="Acá puedes ver los pagos de la nómina."
        variant="body1"
        buttons={buttons}
      />
      <Dialog
        open={generateNomineeOpen}
        maxWidth="lg"
        onClose={() => setGenerateNomineeOpen(false)}
      >
        <GeneratePayoutStep
          stepBack={() => setGenerateNomineeOpen(false)}
          nextStep={handleSuccesStep(() => setGenerateNomineeOpen(false))}
          selectedRequest={sourceId}
          setFailureOpen={setFailure}
        />
      </Dialog>

      <EnhancedTable
        dataHeaders={headers}
        data={payments}
        selected={selectedPayments}
        setSelected={setSelectedPayments}
        canSelectAll={canSelectAll()}
        cantSelectRow={cantSelectRow}
        finalSumRow
      />

      <NomineeExporter
        open={nomineeExporterOpen}
        setOpen={setNomineeExporterOpen}
        payments={payments}
        id={sourceId}
      />
    </div>
  );
};

export default PayOutDetailPayouts;
